import { App, AppState } from '@capacitor/app';
import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as $ from 'jquery';
import { UserProvider } from '../user/user';
import { CrudApiProvider } from '../crud-api/crud-api';
import { ToolsProvider } from '../tools/tools';
import { EventEmitter } from '../../includes/eventEmitter';
import { EnvVariables } from '../../environments/environment';
import { TripService } from '../trip-service/trip-service';
import { PushNotificationsProvider } from '../push-notifications/push-notifications';
import { LocationAccuracy } from '@ionic-native/location-accuracy';
import { AuthProvider } from '../auth/auth';
import { NativeAccessProvider } from '../native-access/native-access';
import { Geolocation } from '@capacitor/geolocation';

const locationMemory: any = {};

const geoCapacitorPort = {
  getCurrentPosition: (a?, b?, o?) => {
    return Geolocation.getCurrentPosition(o).then(
      a || (() => {}),
      b || (() => {})
    );
  },
  watchPosition: (a, b, o) => {
    return Geolocation.watchPosition(o || {}, d => {
      if (d && d.coords) {
        a(d);
      } else {
        b(d);
      }
    });
  },
  clearWatch: d => {
    Geolocation.clearWatch({ id: d });
  },
};

@Injectable()
export class GeolocationProvider extends EventEmitter {
  log = debug('app:GeolocationProvider');

  // 0 - we really don't know
  // 10 - we used IP address to get location
  // 20 - non gps based
  // 30 - gps based
  fallbackLevel = 0;

  lastPosition;
  lastPositionTimestamp;
  bgGeoInited = false;
  focused = document.hasFocus();
  bgGeo: any;
  lastUserId;
  hasSetWatchId;
  watchId;
  hasEscalatedGeolocation;
  fallbackGeoLocation = {
    latitude: 39.8283,
    longitude: -98.5795,
    speed: -1,
  };
  fallbackInterval;
  locationPermissionDenied = false;
  fallbackIntervalCount = 0;

  // there is a circular dependency so google passes itself in
  google: any;

  geoInterval: any = null;

  constructor(
    private platform: Platform,
    private user: UserProvider,
    private crudApi: CrudApiProvider,
    private tripService: TripService,
    private tools: ToolsProvider,
    public env: EnvVariables,
    public push: PushNotificationsProvider,
    private locationAccuracy: LocationAccuracy,
    public auth: AuthProvider,
    public nativeAccess: NativeAccessProvider
  ) {
    super();
    const self = this;
    this.log('geolocation | constructed');

    window['_geolocation'] = this;

    App.addListener('appStateChange', (state: AppState) => {
      if (_.get(state, 'isActive') === false) {
        this.focused = false;
      }
    });

    window.addEventListener('focus', () => {
      this.focused = true;
    });

    document.addEventListener('pause', () => {
      this.focused = false;
    });

    document.addEventListener('resume', () => {
      this.focused = true;
    });

    this.tripService.on('enroute', value => {
      if (!value && this.bgGeoInited) {
        this.bgGeoInited = false;
        this.bgGeo.stop();
        try {
          geoCapacitorPort.clearWatch(this.watchId);
        } catch (e) {}
        this.setIntervals();
      } else if (value && !this.bgGeoInited) {
        this.initBackgroundGeo();
        // initBgGeo takes care of this at the end
        // this.setGeolocationWatch();
      }
    });

    this.auth.on('authenticated', async () => {
      this.setGeolocationWatch();
    });

    if (window['cordova']) {
      // CDLLifeNavigation.addListener('user-location-updated', (data: any) => {
      //   this.log('native navigation sdk location updated', data);
      //   this.turnByTurnUserLocationUpdated(data.lat, data.lon);
      // });
    }

    this.platform.ready().then(async () => {

      if (this.hasBeenInitiated()) {
        return;
      }

      this.setGeolocationWatch()
        .then(res => {
          this.log('geolocation | INIT(): after setGeolocationWatch: ', res);
          // Update every 30s
          this.setIntervals();

          if (!this.tripService.get('isEnroute')) {
            try {
              geoCapacitorPort.clearWatch(this.watchId);
            } catch (e) {}
          }
        })
        .catch(e => {
          this.log(
            'geolocation | INIT(): error after setGeolocationWatch: ',
            e
          );

          if (!this.tripService.get('isEnroute')) {
            try {
              geoCapacitorPort.clearWatch(this.watchId);
            } catch (e) {}
          }
        });
    });
  }

  async getCurrentPosition(opts?): Promise<any> {
    opts = opts || {};

    this.setGeolocationWatch();

    let minLevel = opts.minLevel || 20,
      passRejection = opts.passRejection || false;

    this.log('geolocation | getCurrentPosition(): ', opts, {
      minLevel,
    });
    // opts for just this fn, not to be passed down
    delete opts.minLevel;

    let saveLastPosition = true;

    let t1, t2;
    return new Promise((resolve, reject) => {
      let promise;

      if (this.lastPosition) {
        this.log(
          'geolocation | getCurrentPosition(): resolving with lastPosition',
          this.lastPosition
        );
        promise = Promise.resolve(this.lastPosition);
      } else {
        this.log(
          'geolocation | getCurrentPosition(): getFallBackPosition with opts',
          opts
        );
        promise = this.getFallbackPosition(opts);
      }

      promise
        .then(res => {
          resolve(res);
          this.log(
            'geolocation | getCurrentPosition(): got res updating user position',
            res
          );
          this.tools.emit('updateUserLocation');
          // if (_.get(opts, 'overridePermissions')) {
          //   this.emit('reInitMap');
          // }
        })
        .catch(e => {
          this.log(
            'geolocation | getCurrentPosition(): location error',
            e,
            minLevel
          );

          // we don't let this save
          if (minLevel <= 10) {
            saveLastPosition = false;
            this.getGeolocationFromIP({ setFallback: false }).then(res => {
              resolve(res);
            });
          } else if (passRejection) {
            return reject(e);
          }
        });

      // after 5s, give up.
      //
      t1 = setTimeout(() => {
        if (this.fallbackLevel) {
          resolve(this.fallbackGeoLocation);
        }
      }, 5e3);

      t2 = setInterval(() => {
        if (this.fallbackLevel) {
          resolve(this.fallbackGeoLocation);
        }
      }, 10e3);
    }).then(result => {
      clearTimeout(t1);
      clearInterval(t2);
      this.log('geolocation | getCurrentPosition(): DONE', result);

      if (saveLastPosition) {
        this.locationPermissionDenied = false;
        this.setIntervals();
        this.setLastPosition(result);
      }

      return result;
    });
  }

  updateUsersPosition(lat, lon, speed = 0) {
    this.log(
      'geolocation | updateUsersPosition(): updating location',
      lat,
      lon
    );

    return new Promise((resolve, reject) => {
      var userId = this.user.get('_id'),
        update = {};

      if (lat && lon) {
        resolve({ longitude: lon, latitude: lat });

        if (!userId) return;

        update['latitude'] = lat;
        update['longitude'] = lon;
        update['lastReportedLocation'] = new Date().toISOString();
        update['lastReportedActive'] = new Date().toISOString();

        this.log('geolocation | updateUsersPosition(): updating user location');

        // Push needs to be initialized, or you may miss a badge
        if (!window['cordova'] || this.push.hasSetToken) {
          if (this.focused) {
            this.log('geolocation | updateUsersPosition(): logging event');
            this.tools.logEvent('location', 'inApp', {
              latitude: lat,
              longitude: lon,
              speed: speed,
            });
          } else {
            this.log('geolocation | updateUsersPosition(): not focused');
          }
        } else {
          this.log(
            "geolocation | updateUsersPosition(): not setting because push isn't ready"
          );
        }

        this.crudApi
          .put(`/api/users/${userId}/recentStatus`, update)
          .then(res => {
            this.log(
              'geolocation | updateUsersPosition(): user location updated',
              res
            );
            this.user.mergeUser({
              recentStatus: _.get(res, 'data.recentStatus'),
            });
            this.emit('updateUserLocation');
          })
          .catch(e => {
            this.log(
              'geolocation | updateUsersPosition(): error updating user location',
              e
            );
          });
      } else {
        reject('error getting latitude or longitude');
      }
    });
  }

  // same as get current position, but doesn't requery for it if we have it
  // cached
  getRecentPosition(): Promise<any> {
    if (this.lastPosition) {
      return Promise.resolve(this.lastPosition);
    } else {
      return this.getCurrentPosition();
    }
  }

  getRecentLocationPlace(pos?): Promise<any> {
    return (pos ? Promise.resolve(pos) : this.getRecentPosition()).then(res => {
      this.log('geolocation | will reverse geocode');

      return new Promise((resolve, reject) => {
        if (locationMemory[res.longitude + ', ' + res.latitude]) {
          return resolve(locationMemory[res.longitude + ', ' + res.latitude]);
        }
        $.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            res.longitude + ', ' + res.latitude
          )}.json`,
          {
            access_token: this.env.mapboxglAccessToken,
          }
        ).then(pres => {
          let p = _.find(pres.features, r => {
            return _.includes(r.place_type, 'address');
          });

          if (!p) {
            p = _.first(pres.features);
          }

          resolve(
            (locationMemory[res.longitude + ', ' + res.latitude] = {
              name: 'Current Location',
              description: String(_.get(p, 'place_name') || '').replace(
                ', United States',
                ''
              ),
              placeType: 'CURRENT_LOCATION',
              location: {
                type: 'Point',
                coordinates: [res.longitude, res.latitude],
              },
            })
          );
        });
      }).catch(e => {
        this.log('geolocation | getRecentLocationPlace(): error ');
      });
    });
  }

  hasBeenInitiated() {
    return !!this.hasSetWatchId;
  }

  // Private internal fns------------

  private clearInterval() {
    clearInterval(this.geoInterval);
    this.geoInterval = null;
  }

  private setIntervals() {
    let ran;

    if (!this.geoInterval) {
      clearInterval(this.geoInterval);
      this.geoInterval = setInterval(() => {
        onInterval.apply(this);
      }, 60e3);

      setTimeout(() => {
        this.intervalUpdate();
        onInterval.apply(this);
      }, 100);

      setTimeout(() => {
        if (!ran) {
          onInterval.apply(this);
        }
      }, 1000);

      setTimeout(() => {
        if (!ran) {
          onInterval.apply(this);
        }
      }, 5000);
    }

    function onInterval() {
      const p = this.lastPosition;

      if (p && p.latitude && p.longitude) {
        if (_.get(this.push, 'hasSetToken')) {
          ran = true;
        }

        if (this.user.get('_id')) {
          // only send update if somewhat recent
          if (
            this.lastPositionTimestamp &&
            new Date().getTime() -
              new Date(this.lastPositionTimestamp).getTime() <
              60e3
          ) {
            this.updateUsersPosition(p.latitude, p.longitude, p.speed);
          } else {
          }
        }
      }
    }
  }

  private setGeolocationWatch() {
    if (!this.user.get('_id') || this.hasSetWatchId) {
      this.log(
        'geolocation | setGeolocation(): RETURNING',
        this.user.get('_id'),
        this.hasSetWatchId
      );
      return Promise.resolve(null);
    }

    if (_.get(this, 'watchId')) {
      this.log('geolocation | setGeolocation(): setting geolocation watch');
      try {
        // clearing watch before resetting // safety
        geoCapacitorPort.clearWatch(this.watchId);
      } catch (e) {}
    } else {
      this.log('geolocation | setGeolocation(): resetting geolocation watch');
    }

    return new Promise<void>((resolve, reject) => {
      this.locationAccuracy
        .canRequest()
        .then((canRequest: boolean) => {
          this.log('geolocation | setGeolocation(): canRequest ', canRequest);
          if (canRequest) {
            // the accuracy option will be ignored by iOS
            return this.locationAccuracy
              .request(this.locationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY)
              .then(
                res => {
                  this.log(
                    'geolocation | setGeolocation(): High Res Request successful',
                    res
                  );
                  this.locationPermissionDenied = false;
                  this.setIntervals(); // double check if need be;
                  resolve(res);
                },
                error => {
                  this.log(
                    'geolocation | setGeolocation(): error requesting location permissions',
                    error
                  );

                  this.clearInterval();
                  reject(error);
                }
              )
              .catch(e => {
                this.log(
                  'geolocation | setGeolocation(): error requesting location permissions'
                );
                this.clearInterval();
                reject(e);
              });
          } else {
            this.log(
              'geolocation | setGeolocation(): UNABLE TO REQUEST LOCATION'
            );
            this.locationPermissionDenied = true;
            this.clearInterval();
            reject(canRequest);
          }
        })
        .catch(e => {
          if (window['cordova']) {
            this.log(
              'geolocation | setGeolocation(): error with location accuracy request'
            );
            this.clearInterval();
            reject(e);
          } else {
            return Promise.resolve();
          }
        })
        .then(() => {
          // we will just listed for updates and store on lastPosition, but only
          // update user and db every 30s
          try {
            this.log(
              'geolocation | setGeolocation(): actually setting geolocation watch'
            );

            this.escalateGeolocation();

            this.hasSetWatchId = true;

            this.watchId = geoCapacitorPort.watchPosition(
              _.throttle(res => {
                const lon = _.get(res, 'coords.longitude'),
                  lat = _.get(res, 'coords.latitude'),
                  speed = _.get(res, 'coords.speed');

                this.setLastPosition({
                  longitude: lon,
                  latitude: lat,
                  speed: speed,
                });
                this.setFallback(this.lastPosition, 39);
                this.log(
                  'geolocation | setGeolocation(): highAccuracy Location received'
                );
                this.setIntervals(); // safety check
                this.locationPermissionDenied = false;
                resolve();
              }, 10e3),
              e => {
                this.log('geolocation | error watch', e);
                if (_.get(e, 'code') === 1 || _.get(e, 'PERMISSION_DENIED')) {
                  this.locationPermissionDenied = true;
                  this.log(
                    'geolocation | setGeolocation(): locationPermissionDenied'
                  );
                  reject();
                }
                console.error(
                  'geolocation | setGeolocation(): Error watching geolocation',
                  e
                );
                clearInterval(this.geoInterval);
                try {
                  geoCapacitorPort.clearWatch(this.watchId);
                } catch (e) {}
              },
              { enableHighAccuracy: true, timeout: 5000 }
            );
            this.emit('gotHighAccuracy');
          } catch (e) {
            console.error(
              'geolocation | setGeolocation(): error trying to watch geo',
              e
            );
            this.hasSetWatchId = false;
            clearInterval(this.geoInterval);
            reject();
          }
        });
    });
  }

  private turnByTurnUserLocationUpdated(lat, lon, speed = 0) {
    return new Promise<void>((resolve, reject) => {
      _.throttle(() => {
        this.setLastPosition({
          longitude: lon,
          latitude: lat,
          speed: speed,
        });
        resolve();
      }, 10e3);
    });
  }

  private intervalUpdate() {
    this.log('geolocation | intervalUpdate(): RUNNING');

    // This gets a non high-accuracy position
    if (!this.locationPermissionDenied && this.focused) {
      geoCapacitorPort.getCurrentPosition(
        data => {
          this.setFallback(
            {
              latitude: parseFloat(_.get(data, 'coords.latitude')),
              longitude: parseFloat(_.get(data, 'coords.longitude')),
              speed: -1,
            },
            20
          );
        },
        e => {
          this.log(
            'geolocation | intervalUpdate(): error updateing location',
            e
          );
        },
        {
          enableHighAccuracy: false,
          maximumAge: 1e999,
        }
      );
    }

    this.getCurrentPosition();

    if (this.bgGeo && this.user.get('_id') && !this.locationPermissionDenied) {
      this.log('geolocation | intervalUpdate(); need to init bgGeo');
      this.initBackgroundGeo();
    }
  }

  private initBackgroundGeo() {
    console.log('STUB INITTING BACKGROUND GEO');
    return console.log('background geo removed');
    // this.bgGeo = (<any>window).BackgroundGeolocation;
    // this.log(
    //   'geolocation | initBackgroundGeo(): trying to init bgGeo',
    //   this.bgGeoInited,
    //   this.bgGeo,
    //   this.user.get('_id'),
    //   this.tripService.get('isEnroute')
    // );
    // if (
    //   this.bgGeoInited ||
    //   !this.bgGeo ||
    //   !this.user.get('_id') ||
    //   !this.tripService.get('isEnroute')
    // ) {
    //   if (!this.bgGeo) {
    //     this.log('geolocation | initBackgroundGeo(): web browser CANNOT TRACK');
    //   }
    //   return;
    // } // already inited or not enroute

    // this.bgGeoInited = true;
    // this.log('geolocation | initBackgroundGeo(): initing background geo');

    // const bgGeo = this.bgGeo;

    // this.lastUserId = this.user.get('_id');

    // bgGeo.on(
    //   'http',
    //   res => {
    //     this.log('geolocation | initBackgroundGeo(): HTTP SUCCESS: ', res);
    //   },
    //   e => {
    //     this.log('geolocation | initBackgroundGeo(): HTTP FAILURE', e);
    //   }
    // );

    // this.log(
    //   'geolocation | background',
    //   (this.env.eventAPIURL || this.env.apiURL) +
    //     '/api/events/backgroundLocation'
    // );

    // bgGeo.ready(
    //   {
    //     desiredAccuracy: this.bgGeo.DESIRED_ACCURACY_HIGH,
    //     distanceFilter: 20,
    //     url:
    //       (this.env.eventAPIURL || this.env.apiURL) +
    //       '/api/events/backgroundLocation',
    //     stopOnTerminate: false,
    //     startOnBoot: true,
    //     reset: true,
    //     method: 'POST',
    //     headers: {
    //       'Turnkey-Auth': CrudApiProvider.authKey || '',
    //     },
    //     notification: {
    //       title: 'Active Trip',
    //       text: 'You are in an Active Trip with CDLLife',
    //     },
    //     params: {},
    //   },
    //   state => {
    //     // 3.  Start tracking
    //     this.log(
    //       'geolocation | initBackgroundGeo(): is configured and ready to use',
    //       state
    //     );
    //     if (!state.enabled) {
    //       bgGeo.start(() => {
    //         this.log('started');
    //         this.setGeolocationWatch(); // safety check to make sure geo watch is set
    //       });
    //     }
    //   }
    // );
  }

  private getFallbackPosition(opts?) {
    this.log('geolocaiton | getFallBackPosition(): ', opts);
    if (
      (!this.locationPermissionDenied && this.focused) ||
      _.get(opts, 'overridePermissions')
    ) {
      this.log('Geolocation.getCurrentPosition');
      return Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        maximumAge: 1e999,
      })
        .then(res => {
          this.log('geolocaiton | getFallbackPosition(): response good', res);
          const lon = _.get(res, 'coords.longitude'),
            lat = _.get(res, 'coords.latitude'),
            speed = _.get(res, 'coords.speed');
          return Promise.resolve({
            longitude: lon,
            latitude: lat,
            speed: speed,
          });
        })
        .catch(e => {
          this.log(
            'geolocation | getFallbackPosition(): error getFallbackPosition',
            e
          );
          this.locationPermissionDenied = true;
          return Promise.reject(null);
        });
    } else {
      this.log(
        'geolocation | getFallbackPosition(): no focus, permissions, or overrides'
      );
      return Promise.reject(null);
    }
  }

  private setFallback(location, level) {
    if (this.fallbackLevel <= level) {
      this.log('geolocation | setFallback(): ', level, location);
      this.fallbackGeoLocation = location;
      this.fallbackLevel = level;
      this.setLastPosition(location);

      // if more than IP addr
      if (level > 10) {
        this.emit('updateUserLocation');
        clearInterval(this.fallbackInterval);
      }
    } else {
      this.log('geolocation | setFallback():  skipping', level, location);
    }
  }

  private getGeolocationFromIP(opts: any = {}) {
    let { setFallback } = opts;
    return new Promise((resolve, reject) => {
      const settings = {
        url: 'https://www.googleapis.com/geolocation/v1/geolocate?key=AIzaSyAua-U19AcpWvpNRI023n14I83lg84G094',
        method: 'POST',
        processData: false,
        headers: {
          'Content-Type': 'application/json',
        },
        data: JSON.stringify({ considerIp: true }),
      };

      $.ajax(settings)
        .done(response => {
          const lat = _.get(response, 'location.lat'),
            lon = _.get(response, 'location.lng');
          this.log('got geolocation from ip');
          if (lat && lon) {
            if (setFallback) {
              this.fallbackLevel = 10;
              this.setFallback(
                {
                  longitude: lon,
                  latitude: lat,
                  speed: -1,
                },
                10
              );
            }
            resolve({
              longitude: lon,
              latitude: lat,
              speed: -1,
            });
          }
        })
        .fail(e => {
          console.error('fallback error', e);
        });
    });
  }

  private escalateGeolocation() {
    if (this.hasEscalatedGeolocation) {
      return;
    }

    this.hasEscalatedGeolocation = true;

    const self = this;

    nextStep(0);

    function calculatePosition(timeout, maxAge, accuracy, step, fallbackScore) {
      self.log('geolocation | escalateGeolocation(): calculatePosition', step);
      if (!self.locationPermissionDenied && self.focused) {
        geoCapacitorPort.getCurrentPosition(
          res => {
            const lon = _.get(res, 'coords.longitude'),
              lat = _.get(res, 'coords.latitude'),
              speed = _.get(res, 'coords.speed');

            self.log('geolocation | escalateGeolocation(): success', step);
            self.setFallback(
              {
                longitude: lon,
                latitude: lat,
                speed: speed || -1,
              },
              fallbackScore
            );
          },
          error => {
            nextStep(step + 1);
          },
          { enableHighAccuracy: accuracy, timeout: timeout, maximumAge: maxAge }
        );
      }
    }

    function nextStep(step) {
      switch (step) {
        case 0:
          calculatePosition(1000, 604800000, false, step, 21);
          break;
        case 1:
          calculatePosition(1000, 604800000, true, step, 31);
          break;
        case 2:
          calculatePosition(5000, 4000, false, step, 21);
          break;
        case 3:
          calculatePosition(5000, 4000, true, step, 31);
          break;
        case 4:
          calculatePosition(50000, 60000, true, step, 31);
          break;
        case 5:
          calculatePosition(50000, 60000, false, step, 21);
          break;
        case 5:
          calculatePosition(50000, 60000, false, step - 3, 21);
          break;
      }
    }
  }

  private setLastPosition(position) {
    if (position) {
      this.tools.emit('updateUserLocation');
      this.lastPosition = position;
      this.lastPositionTimestamp = new Date();
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/geolocation/geolocation.ts